import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import deviceGroupService from "../../services/deviceGroupService";
import vppCatalogService from "../../services/vppCatalogService";
import macPackageService from "../../services/macPackageService";
import { getDdmStatus, getDeclarations } from "../../services/nanoDdmService";

function packageUpdatePayload(pkg, { groups, udids }) {
  return {
    name: pkg.name,
    description: pkg.description || "",
    title: pkg.title || pkg.name,
    bundleIdentifier: pkg.bundleIdentifier,
    bundleVersion: pkg.bundleVersion,
    removeFilesOnUninstall: pkg.removeFilesOnUninstall !== false,
    installRequired: pkg.installRequired !== false,
    deliveryMode: pkg.deliveryMode || "auto",
    enabled: pkg.enabled !== false,
    groups,
    udids,
  };
}

function isAssignableDeclaration(decl) {
  const identifier = decl?.Identifier || decl?.identifier || "";
  const type = String(decl?.Type || decl?.type || "");
  if (!identifier || String(identifier).startsWith("xavier.")) return false;
  if (type.includes("softwareupdate.enforcement")) return false;
  return true;
}

function declarationLabel(decl) {
  const identifier = decl?.Identifier || decl?.identifier || "";
  const type = String(decl?.Type || decl?.type || "");
  const shortType = type.replace(/^com\.apple\.(configuration|activation|asset|management)\./, "");
  return { identifier, type, shortType };
}

function deviceMeta(device) {
  return {
    serialNumber: device?.SerialNumber,
    deviceName: device?.QueryResponses?.DeviceName,
    assetTag: device?.assetTag,
    location: device?.location?._id || undefined,
  };
}

export default function DeviceAssignmentModal({ visible, device, initialTab = "groups", onClose, onSaved }) {
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsSel, setGroupsSel] = useState([]);
  const [declarationsSel, setDeclarationsSel] = useState([]);
  const [extraApps, setExtraApps] = useState([]);
  const [pendingApps, setPendingApps] = useState([]);
  const [appConfigs, setAppConfigs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState("");
  const [ddmAvailable, setDdmAvailable] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packagesSel, setPackagesSel] = useState([]);
  const [ready, setReady] = useState(false);

  const udid = device?.UDID;

  useEffect(() => {
    if (!visible || !device) return;
    setTab(initialTab || "groups");
    setCatalogQuery("");
    setPendingApps([]);
    setPackages([]);
    setPackagesSel([]);
    setReady(false);
    setLoading(true);
    Promise.all([
      deviceGroupService.getDeviceGroups().catch(() => []),
      deviceGroupService.getDeviceAssignments(udid, device.SerialNumber).catch(() => ({
        groups: [],
        declarations: [],
        extraApps: [],
      })),
      vppCatalogService.getAppConfigs().catch(() => []),
      vppCatalogService.getAssets().catch(() => ({ assets: [] })),
      getDdmStatus().catch(() => ({ available: false })),
      getDeclarations({ detail: true }).catch(() => ({ declarations: [] })),
      macPackageService.listPackages().catch(() => []),
    ])
      .then(([groupList, assignments, configs, catalog, status, listed, pkgRows]) => {
        setGroups(Array.isArray(groupList) ? groupList : []);
        setGroupsSel((assignments.groups || []).map((item) => String(item.groupId)));
        setDeclarationsSel(Array.isArray(assignments.declarations) ? [...assignments.declarations] : []);
        setExtraApps(assignments.extraApps || []);
        setAppConfigs(configs || []);
        setAssets(catalog.assets || []);
        setDdmAvailable(Boolean(status?.available));
        const rows = listed.declarations || [];
        setDeclarations(rows.filter(isAssignableDeclaration));
        const list = Array.isArray(pkgRows) ? pkgRows : [];
        setPackages(list);
        const deviceUdid = String(udid || "");
        setPackagesSel(
          list
            .filter((pkg) => (pkg.udids || []).map(String).includes(deviceUdid))
            .map((pkg) => String(pkg._id)),
        );
        setReady(true);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || err.message || "Zuweisungen konnten nicht geladen werden.");
        setReady(false);
      })
      .finally(() => setLoading(false));
  }, [visible, device, initialTab, udid]);

  const assignedAdam = useMemo(() => {
    const ids = new Set([
      ...extraApps.map((app) => String(app.adamId)),
      ...pendingApps.map((app) => String(app.adamId)),
    ]);
    return ids;
  }, [extraApps, pendingApps]);

  const catalogVisible = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    return (assets || []).filter((app) => {
      if (assignedAdam.has(String(app.adamId))) return false;
      if (!q) return true;
      return [app.name, app.artistName, app.sellerName, app.bundleId, app.adamId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [assets, assignedAdam, catalogQuery]);

  if (!visible || !device) return null;

  const toggleGroup = (id) => {
    setGroupsSel((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleDeclaration = (identifier) => {
    setDeclarationsSel((prev) => (
      prev.includes(identifier)
        ? prev.filter((item) => item !== identifier)
        : [...prev, identifier]
    ));
  };

  const togglePackage = (id) => {
    setPackagesSel((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const addApp = (app) => {
    setPendingApps((prev) => [
      ...prev,
      {
        adamId: app.adamId,
        bundleId: app.bundleId || "",
        name: app.name || app.adamId,
        pricingParam: app.pricingParam || "STDQ",
        productType: app.productType || "App",
        managedAppConfig: "",
      },
    ]);
  };

  const save = async () => {
    if (!udid) {
      toast.error("UDID fehlt. Gerät ist nicht eingeschrieben.");
      return;
    }
    if (!device.SerialNumber) {
      toast.error("Seriennummer fehlt.");
      return;
    }
    setSaving(true);
    const meta = deviceMeta(device);
    try {
      await deviceGroupService.setDeviceGroups(udid, { groupIds: groupsSel, ...meta });
      await deviceGroupService.setDeviceDeclarations(udid, { identifiers: declarationsSel, ...meta });
      for (const app of pendingApps) {
        await deviceGroupService.assignDeviceApp(udid, {
          ...meta,
          adamId: app.adamId,
          bundleId: app.bundleId,
          name: app.name,
          pricingParam: app.pricingParam,
          productType: app.productType,
          managedAppConfig: app.managedAppConfig || undefined,
        });
      }
      const wanted = new Set(packagesSel);
      const deviceUdid = String(udid);
      for (const pkg of packages) {
        const pkgId = String(pkg._id);
        const currentUdids = (pkg.udids || []).map(String);
        const has = currentUdids.includes(deviceUdid);
        const want = wanted.has(pkgId);
        if (has === want) continue;
        const nextUdids = want
          ? [...new Set([...currentUdids, deviceUdid])]
          : currentUdids.filter((id) => id !== deviceUdid);
        await macPackageService.updatePackage(
          pkgId,
          packageUpdatePayload(pkg, {
            groups: (pkg.groups || []).map((g) => String(g._id || g)),
            udids: nextUdids,
          }),
        );
      }
      toast.success("Zuweisungen gespeichert.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const title = {
    groups: "Gruppe zuweisen",
    apps: "App zuweisen",
    packages: "Paket zuweisen",
    ddm: "DDM zuweisen",
  }[tab] || "Zuweisen";

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {title} · {device.QueryResponses?.DeviceName || device.SerialNumber}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <ul className="nav nav-tabs mb-3">
              {[
                ["groups", `Gruppen (${groupsSel.length})`],
                ["packages", `Pakete (${packagesSel.length})`],
                ["apps", `Apps (${extraApps.length + pendingApps.length})`],
                ["ddm", `DDM (${declarationsSel.length})`],
              ].map(([id, label]) => (
                <li className="nav-item" key={id}>
                  <button
                    type="button"
                    className={`nav-link ${tab === id ? "active" : ""}`}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>

            {loading ? <p className="text-muted">Laden…</p> : null}

            {!loading && tab === "groups" && (
              <div>
                <p className="small text-muted">
                  Das Gerät kann mehreren Gruppen angehören. Profile, Apps und DDM der Gruppen gelten danach für dieses Gerät – auch bei User Enrollment.
                </p>
                <div className="d-flex flex-column gap-1">
                  {groups.map((group) => {
                    const id = String(group._id);
                    return (
                      <label key={id} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={groupsSel.includes(id)}
                          onChange={() => toggleGroup(id)}
                        />
                        <span className="form-check-label">{group.name}</span>
                      </label>
                    );
                  })}
                  {groups.length === 0 && (
                    <p className="text-muted mb-0">
                      Noch keine Gruppen. Bitte unter <Link to="/device-groups">Geräte-Gruppen</Link> anlegen.
                    </p>
                  )}
                </div>
              </div>
            )}

            {!loading && tab === "packages" && (
              <div>
                <p className="small text-muted">
                  Direkte .pkg-Zuweisung für dieses Gerät (zusätzlich zu Gruppen). Katalog unter{" "}
                  <Link to="/mac-packages">Mac-Pakete</Link>.
                  Pakete nur über Gruppen erscheinen hier nicht als Haken — sie kommen über den Tab Gruppen.
                </p>
                <div className="d-flex flex-column gap-1">
                  {packages.map((pkg) => {
                    const id = String(pkg._id);
                    const viaGroup = (pkg.groups || []).some((g) => groupsSel.includes(String(g._id || g)));
                    return (
                      <label key={id} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={packagesSel.includes(id)}
                          onChange={() => togglePackage(id)}
                          disabled={!pkg.hasFile}
                        />
                        <span className="form-check-label">
                          {pkg.name}
                          {!pkg.hasFile ? (
                            <span className="small text-warning"> · Datei fehlt</span>
                          ) : (
                            <span className="small text-muted"> · {pkg.bundleIdentifier}</span>
                          )}
                          {viaGroup ? (
                            <span className="small text-muted"> · bereits über Gruppe</span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                  {packages.length === 0 && (
                    <p className="text-muted mb-0">
                      Noch keine Pakete. Unter <Link to="/mac-packages">Mac-Pakete</Link> anlegen.
                    </p>
                  )}
                </div>
              </div>
            )}

            {!loading && tab === "apps" && (
              <div>
                <p className="small text-muted">
                  Zusätzliche Apps gelten nur für dieses Gerät. Apps aus Gruppen bleiben bestehen.
                </p>
                <h6>Diesem Gerät zugewiesen</h6>
                {extraApps.length === 0 && pendingApps.length === 0 ? (
                  <p className="small text-muted">Keine gerätespezifischen Apps.</p>
                ) : (
                  <ul className="mb-3">
                    {extraApps.map((app) => (
                      <li key={app.adamId}>
                        {app.name || app.adamId}
                        {app.configName ? <span className="small text-muted"> · {app.configName}</span> : null}
                        {app.disabled ? <span className="badge text-bg-warning ms-1">deaktiviert</span> : null}
                      </li>
                    ))}
                    {pendingApps.map((app) => (
                      <li key={app.adamId} className="mb-2">
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <span>{app.name || app.adamId}</span>
                          <span className="small text-muted">neu</span>
                          <select
                            className="form-select form-select-sm"
                            style={{ maxWidth: 260 }}
                            value={app.managedAppConfig || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setPendingApps((prev) =>
                                prev.map((item) =>
                                  item.adamId === app.adamId ? { ...item, managedAppConfig: value } : item
                                )
                              );
                            }}
                          >
                            <option value="">Keine Managed App Config</option>
                            {appConfigs
                              .filter((cfg) => String(cfg.adamId) === String(app.adamId))
                              .map((cfg) => (
                                <option key={cfg._id} value={cfg._id}>{cfg.name}</option>
                              ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-sm btn-link"
                            onClick={() => setPendingApps((prev) => prev.filter((item) => item.adamId !== app.adamId))}
                          >
                            Entfernen
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <h6>Aus Katalog hinzufügen</h6>
                <input
                  className="form-control form-control-sm mb-2"
                  placeholder="App suchen…"
                  value={catalogQuery}
                  onChange={(e) => setCatalogQuery(e.target.value)}
                />
                <div style={{ maxHeight: 240, overflow: "auto" }}>
                  {catalogVisible.slice(0, 40).map((app) => (
                    <div key={app.adamId} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                      <div>
                        <div>{app.name || app.adamId}</div>
                        <div className="small text-muted">{app.artistName || app.sellerName || app.bundleId}</div>
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addApp(app)}>
                        Hinzufügen
                      </button>
                    </div>
                  ))}
                  {catalogVisible.length === 0 && (
                    <p className="small text-muted mb-0">
                      Keine weiteren Apps. Katalog unter <Link to="/applications">Applications</Link>.
                    </p>
                  )}
                </div>
              </div>
            )}

            {!loading && tab === "ddm" && (
              <div>
                {!ddmAvailable ? (
                  <p className="text-muted mb-0">
                    NanoHUB DDM ist nicht konfiguriert. Deklarationen zuerst unter{" "}
                    <Link to="/ddm">DDM</Link> anlegen, sobald DDM erreichbar ist.
                  </p>
                ) : (
                  <>
                    <p className="small text-muted">
                      Zusätzliche Deklarationen nur für dieses Gerät. Gruppen-DDM bleibt über die Gruppenzuweisung.
                      OS-Update wird nicht hier gesetzt, sondern unter{" "}
                      <Link to="/software-updates">Software Updates</Link> bzw. in der Gerätegruppe.
                    </p>
                    <div className="d-flex flex-column gap-1">
                      {declarations.map((decl) => {
                        const meta = declarationLabel(decl);
                        return (
                          <label key={meta.identifier} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={declarationsSel.includes(meta.identifier)}
                              onChange={() => toggleDeclaration(meta.identifier)}
                            />
                            <span className="form-check-label">
                              <code>{meta.identifier}</code>
                              {meta.shortType ? (
                                <span className="small text-muted"> · {meta.shortType}</span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                      {declarations.length === 0 && (
                        <p className="text-muted mb-0">Noch keine benutzerdefinierten Deklarationen.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Abbrechen</button>
            <button type="button" className="btn btn-primary" disabled={saving || loading || !ready} onClick={save}>
              {saving ? "Speichere…" : "Zuweisungen speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
